// Fichier: /components/layout/PublicFooter.tsx (ou équivalent)
// Version adaptée pour SIS avec la nouvelle charte graphique et le contenu pertinent.

import React from 'react';
import { Link } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';

// Icônes Lucide pertinentes pour SIS
import { 
    Instagram, 
    Linkedin, 
    Mail, 
    Phone, 
    MapPin, 
    Home, 
    Info, 
    Facebook,
    Twitter,
    Wrench,     // Pour les services/maintenance
    Server,     // Pour l'infrastructure
    Headset,    // Pour le support/contact
    Send,
    Code,       // Pour le développement
    HardDrive   // Icône de fallback pour le logo
} from 'lucide-react';

interface PublicFooterProps {
    isDarkMode?: boolean;
}

export const PublicFooter: React.FC<PublicFooterProps> = ({ isDarkMode = false }) => {
    const currentYear = new Date().getFullYear();
    const { tenant } = useTenantContext();

    // Le footer utilise le nouveau fond bleu nuit pour une meilleure cohérence
    const footerClasses = "relative overflow-hidden bg-[#08062b] text-white";

    return (
        <footer className={footerClasses}>
            {/* Ligne décorative supérieure avec la couleur ORANGE de SIS */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-transparent via-[#fe8d17]/80 to-transparent" />
            
            {/* Particules flottantes en arrière-plan avec la nouvelle palette SIS */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-20 w-32 h-32 bg-[#fe8d17]/8 rounded-full blur-3xl animate-pulse" />
                <div className="absolute top-40 right-40 w-40 h-40 bg-[#008aef]/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                <div className="absolute bottom-20 left-1/3 w-24 h-24 bg-white/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }} />
            </div>
            
            <div className="max-w-screen-2xl mx-auto py-20 px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                
                    {/* --- Colonne 1: Identité de SIS --- */}
                    <div className="group relative space-y-6 p-8 rounded-3xl bg-white/5 backdrop-blur-md ring-1 ring-white/10 transition-all duration-500 hover:bg-white/10 hover:ring-[#fe8d17]/30 overflow-hidden flex flex-col">
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform rotate-12 scale-150" />
                        
                        <Link to="/" className="group/logo relative flex flex-col items-center text-center gap-4">
                           <div className="relative flex-shrink-0">
                               {tenant?.logo ? (
                                   <img src={tenant.logo} alt={`Logo ${tenant?.name || 'SIS'}`} className="h-28 w-auto bg-gradient-to-br from-white/10 to-transparent p-2 rounded-2xl shadow-2xl ring-2 ring-white/20 group-hover/logo:ring-[#fe8d17]/50 transition-all duration-300 group-hover/logo:scale-105 object-contain" />
                               ) : (
                                   <div className="h-28 w-28 bg-gradient-to-br from-white/10 to-transparent p-2 rounded-2xl shadow-2xl ring-2 ring-white/20 group-hover/logo:ring-[#fe8d17]/50 transition-all duration-300 group-hover/logo:scale-105 flex items-center justify-center">
                                       <HardDrive className="h-14 w-14 text-white" />
                                   </div>
                               )}
                               <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#fe8d17]/60 rounded-full animate-pulse opacity-0 group-hover/logo:opacity-100 transition-opacity duration-300" />
                           </div>

                           <div className="relative z-10">
                                <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-[#008aef] bg-clip-text text-transparent group-hover/logo:from-[#008aef] group-hover/logo:to-white transition-all duration-300">
                                    {tenant?.name || 'Solution Informatique Services'}
                                </h3><br/>
                           </div>
                        </Link>

                        <div className="mt-auto space-y-4">
                            <p className="relative z-10 text-gray-300 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                            Votre partenaire pour une transformation numérique réussie. Partenaire technologique au Congo : maintenance, réseaux, développement et support technique.
                            </p>
                            </div>
                    </div>

                    {/* --- Colonne 2: Liens Rapides (adaptés pour SIS) --- */}
                    <div className="group relative space-y-6 p-8 rounded-3xl bg-white/5 backdrop-blur-md ring-1 ring-white/10 transition-all duration-500 hover:bg-white/10 hover:ring-[#008aef]/30 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform rotate-12 scale-150" />
                        <div className="relative z-10">
                            <h3 className="text-xl font-bold bg-gradient-to-r from-[#008aef] to-[#007acc] bg-clip-text text-transparent mb-6 flex items-center">
                                <Code className="w-5 h-5 text-[#008aef] mr-2 animate-pulse" />
                                LIENS UTILES
                            </h3>
                            <nav className="space-y-4">
                                <Link to="/" className="group/link flex items-center gap-3 text-gray-300 hover:text-white transition-all duration-300 p-3 rounded-xl hover:bg-white/5">
                                    <Home className="w-5 h-5 text-[#fe8d17] group-hover/link:scale-110 transition-transform duration-300" />
                                    <span className="font-medium">Accueil</span>
                                </Link>
                                <Link to="/#services" className="group/link flex items-center gap-3 text-gray-300 hover:text-white transition-all duration-300 p-3 rounded-xl hover:bg-white/5">
                                    <Wrench className="w-5 h-5 text-[#fe8d17] group-hover/link:scale-110 transition-transform duration-300" />
                                    <span className="font-medium">Nos services</span>
                                </Link>
                                <Link to="/about" className="group/link flex items-center gap-3 text-gray-300 hover:text-white transition-all duration-300 p-3 rounded-xl hover:bg-white/5">
                                    <Info className="w-5 h-5 text-[#fe8d17] group-hover/link:scale-110 transition-transform duration-300" />
                                    <span className="font-medium">À propos de SIS</span>
                                </Link>
                                <Link to="/contact" className="group/link flex items-center gap-3 text-gray-300 hover:text-white transition-all duration-300 p-3 rounded-xl hover:bg-white/5">
                                    <Headset className="w-5 h-5 text-[#fe8d17] group-hover/link:scale-110 transition-transform duration-300" />
                                    <span className="font-medium">Contactez-nous</span>
                                </Link>
                            </nav>
                        </div>
                    </div>

                    {/* --- Colonne 3: Contact & Réseaux --- */}
                    <div className="group relative space-y-6 p-8 rounded-3xl bg-white/5 backdrop-blur-md ring-1 ring-white/10 transition-all duration-500 hover:bg-white/10 hover:ring-[#fe8d17]/30 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform rotate-12 scale-150" />
                        <div className="relative z-10">
                            <h3 className="text-xl font-bold bg-gradient-to-r from-[#fe8d17] to-[#e47c15] bg-clip-text text-transparent mb-6 flex items-center">
                                <Headset className="w-5 h-5 text-[#fe8d17] mr-2 animate-pulse" />
                                CONTACT & RÉSEAUX
                            </h3>
                            <ul className="space-y-4">
                                {tenant?.config?.phone && (
                                    <li className="group/contact flex items-center space-x-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300">
                                        <Phone className="h-5 w-5 text-[#008aef] group-hover/contact:scale-110 transition-transform duration-300" />
                                        <a href={`tel:${tenant.config.phone}`} className="text-gray-300 hover:text-white transition-colors font-medium">
                                            {tenant.config.phone}
                                        </a>
                                    </li>
                                )}
                                {tenant?.email && (
                                    <li className="group/contact flex items-center space-x-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300">
                                        <Mail className="h-5 w-5 text-[#008aef] group-hover/contact:scale-110 transition-transform duration-300" />
                                        <a href={`mailto:${tenant.email}`} className="text-gray-300 hover:text-white transition-colors font-medium">
                                            {tenant.email}
                                        </a>
                                    </li>
                                )}
                                {tenant?.config?.city && (
                                    <li className="group/contact flex items-start space-x-3 p-3 rounded-xl hover:bg-white/5 transition-all duration-300">
                                        <MapPin className="h-5 w-5 text-[#008aef] mt-1 flex-shrink-0 group-hover/contact:scale-110 transition-transform duration-300" />
                                        <span className="text-gray-300 group-hover/contact:text-white transition-colors font-medium">
                                            {tenant.config.city}, {tenant.config.country}
                                        </span>
                                    </li>
                                )}
                            </ul>
                            <div className="flex gap-4 pt-6">
                                {tenant?.config?.social?.facebook && ( <a href={tenant.config.social.facebook} target="_blank" rel="noopener noreferrer" className="p-3 rounded-xl bg-white/5 hover:bg-[#008aef]/20 text-white transition-all duration-300 hover:scale-110"><Facebook className="w-5 h-5"/></a> )}
                                {tenant?.config?.social?.instagram && ( <a href={tenant.config.social.instagram} target="_blank" rel="noopener noreferrer" className="p-3 rounded-xl bg-white/5 hover:bg-[#008aef]/20 text-white transition-all duration-300 hover:scale-110"><Instagram className="w-5 h-5"/></a> )}
                                {tenant?.config?.social?.linkedin && ( <a href={tenant.config.social.linkedin} target="_blank" rel="noopener noreferrer" className="p-3 rounded-xl bg-white/5 hover:bg-[#008aef]/20 text-white transition-all duration-300 hover:scale-110"><Linkedin className="w-5 h-5"/></a> )}
                                {tenant?.config?.social?.x && ( <a href={tenant.config.social.x} target="_blank" rel="noopener noreferrer" className="p-3 rounded-xl bg-white/5 hover:bg-[#008aef]/20 text-white transition-all duration-300 hover:scale-110"><Twitter className="w-5 h-5"/></a> )}
                            </div>
                        </div>
                    </div>

                    {/* --- Colonne 4: Newsletter --- */}
                    <div className="group relative space-y-6 p-8 rounded-3xl bg-white/5 backdrop-blur-md ring-1 ring-white/10 transition-all duration-500 hover:bg-white/10 hover:ring-[#008aef]/30 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform rotate-12 scale-150" />
                        <div className="relative z-10">
                            <h3 className="text-xl font-bold bg-gradient-to-r from-[#008aef] to-[#007acc] bg-clip-text text-transparent mb-6 flex items-center">
                                <Send className="w-5 h-5 text-[#008aef] mr-2 animate-pulse" />
                                RESTEZ INFORMÉ
                            </h3>
                            <p className="text-gray-300 text-sm leading-relaxed mb-6 group-hover:text-gray-200 transition-colors duration-300">
                                Abonnez-vous à notre newsletter pour recevoir des conseils techniques et des offres exclusives.
                            </p>
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div className="flex flex-col gap-4">
                                    <input
                                        type="email"
                                        placeholder="Votre adresse e-mail"
                                        className="w-full px-5 py-3 text-gray-900 placeholder-gray-500 bg-white/95 rounded-xl border-2 border-transparent focus:ring-2 focus:ring-[#fe8d17] focus:border-[#fe8d17] transition-all duration-300"
                                        aria-label="Adresse e-mail pour la newsletter"
                                    />
                                    <button
                                        type="submit"
                                        className="bg-[#fe8d17] hover:bg-[#e47c15] text-white font-bold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                                    >
                                        S'abonner
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* --- Copyright et Développeur --- */}
                <div className="relative border-t border-white/10 mt-16 pt-12">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-0.5 bg-gradient-to-r from-transparent via-[#fe8d17]/60 to-transparent" />
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0 relative z-10">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-[#008aef] to-[#007acc] rounded-full flex items-center justify-center shadow-lg">
                                <Server className="w-4 h-4 text-white" />
                            </div>
                            <p className="text-gray-300 text-sm font-medium">
                                &copy; {currentYear} <span className="text-[#fe8d17] font-bold">{tenant?.name || 'SIS'}</span>. Tous droits réservés.
                            </p>
                        </div>
                        <div className="text-gray-400 text-sm">
                            Développé avec <Wrench className="w-3 h-3 inline text-[#fe8d17] animate-pulse" /> par{' '}
                            <a 
                                href="https://teles-hosting.com" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#008aef] hover:text-white font-bold transition-colors duration-300"
                            >
                                TELES HOSTING
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default PublicFooter;